/**
 * Send Notification Step Handler
 * Sends Email (Sendgrid) or WhatsApp notifications
 *
 * NOTE: This is a frontend validation handler.
 * Actual sending is delegated to the Edge Function (process-workflows)
 */

import type {
  StepExecutionContext,
  StepExecutionResult,
  SendNotificationStepConfig
} from '../../types/workflow';

export async function executeSendNotification(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as SendNotificationStepConfig;

    // Validate required fields
    if (!config.notification_type) {
      return {
        success: false,
        error: 'Missing required field: notification_type',
      };
    }

    // Validate message template(s) - depends on whatsapp_recipients for WhatsApp
    if (config.notification_type === 'whatsapp' && config.whatsapp_recipients === 'both') {
      // For 'both': require separate messages
      if (!config.message_template_lead) {
        return {
          success: false,
          error: 'Missing required field: message_template_lead (required when sending to both)',
        };
      }
      if (!config.message_template_sales_rep) {
        return {
          success: false,
          error: 'Missing required field: message_template_sales_rep (required when sending to both)',
        };
      }
    } else {
      // For single recipient or email: require single message_template
      if (!config.message_template) {
        return {
          success: false,
          error: 'Missing required field: message_template',
        };
      }
    }

    // Validate email-specific requirements
    if (config.notification_type === 'email') {
      if (!config.subject_template) {
        return {
          success: false,
          error: 'Missing required field for email: subject_template',
        };
      }
      // NOTE: sendgrid_domain_id no longer required - uses org default
    }

    // Validate WhatsApp-specific requirements
    if (config.notification_type === 'whatsapp') {
      if (!config.whatsapp_config_id) {
        return {
          success: false,
          error: 'Missing required field for WhatsApp: whatsapp_config_id',
        };
      }

      // Validate whatsapp_recipients (default to 'lead' for retrocompatibility)
      const recipients = config.whatsapp_recipients || 'lead';
      if (!['lead', 'sales_rep', 'both'].includes(recipients)) {
        return {
          success: false,
          error: 'Invalid whatsapp_recipients value. Must be: lead, sales_rep, or both',
        };
      }

      // Note: Validation of phone number availability is deferred to Edge Function
      // as it depends on the recipient type and entity data
    }

    // Validate email recipients configuration
    if (config.notification_type === 'email') {
      if (!config.recipients) {
        return {
          success: false,
          error: 'Missing required field: recipients',
        };
      }

      if (config.recipients === 'specific_users' && (!config.recipient_user_ids || config.recipient_user_ids.length === 0)) {
        return {
          success: false,
          error: 'No users selected for specific_users recipient type',
        };
      }
    }

    // Validation passed - actual sending will be handled by Edge Function
    console.log(`✅ Send notification step validated:`, {
      type: config.notification_type,
      recipients: config.recipients,
      workflowId: context.workflow.id,
      entityId: context.entity.entity_id,
    });

    // Return success and let Edge Function handle the actual sending
    return {
      success: true,
      nextStepId: context.step.next_step_id,
      data: {
        validated: true,
        notification_type: config.notification_type,
        recipients: config.recipients
      },
    };
  } catch (error) {
    console.error('Error in sendNotification handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
